/* Dubai Exclusives carousel.
 *
 * Externalised from sections/dubai-exclusives-carousel.liquid so it is cached
 * across page loads and does not execute during HTML parsing, per item 5 of
 * docs/PERFORMANCE_OPTIMIZATIONS_2026-04-13.md.
 *
 * Photos start after the hero loads, once the section is within
 * PREFETCH_MARGIN of view. The
 * homepage LCP element is the 100vh hero in section 1, which preloads itself
 * with fetchpriority="high"; pulling carousel photography during initial load
 * would compete with it for bandwidth.
 */
(function () {
  if (customElements.get('dubai-exclusives-carousel')) return;

  /* Chrome's own lazy-load distance on fast connections: far enough ahead that
     a reader scrolling down does not reach the section before its photos. */
  const PREFETCH_MARGIN = '0px 0px 1250px 0px';

  /* The section sits right under the hero, so it is inside that margin at
     first paint. Wait for the hero (the page's fetchpriority="high" image) so
     it stays first in line, but not for window load: load also waits for
     every app and tracker. On Dubai Exclusives the carousel is half in view
     at first paint and its photo slot stayed blank until load (measured
     2026-10-19: grid photos requested at 537ms, carousel at 2308ms = load). */
  const afterLoad = (fn) => {
    let done = false;
    const go = () => {
      if (!done) {
        done = true;
        fn();
      }
    };
    if (document.readyState === 'complete') return go();
    window.addEventListener('load', go, { once: true });
    const ready = () => {
      const hero = document.querySelector('img[fetchpriority="high"]');
      if (!hero || hero.complete) return go();
      hero.addEventListener('load', go, { once: true });
      hero.addEventListener('error', go, { once: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true });
    else ready();
  };

  class DubaiExclusivesCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.dxe__slide'));
      this.dots = Array.from(this.querySelectorAll('.dxe__dot'));
      if (this.slides.length < 2) {
        this.setAttribute('data-playing', 'false');
        /* No autoplay, no arrows, nothing to advance -- but the single slide
           still ships with a parked data-src and no src, which
           pagespeed-critical.liquid hides until something swaps it in. Keep
           the fetch behind the same visibility gate the multi-slide path
           uses so this does not cost anything at page load. */
        if ('IntersectionObserver' in window) {
          afterLoad(() =>
            new IntersectionObserver((entries, obs) => {
              if (entries[0].isIntersecting) {
                this.hydrate(0);
                obs.disconnect();
              }
            }, { rootMargin: PREFETCH_MARGIN }).observe(this)
          );
          // On screen before window load: fetch now rather than wait.
          new IntersectionObserver((entries, obs) => {
            if (entries[0].isIntersecting) {
              this.hydrate(0);
              obs.disconnect();
            }
          }, { threshold: 0.2 }).observe(this);
        } else {
          this.hydrate(0);
        }
        return;
      }

      this.index = 0;
      this.timer = null;
      this.userPaused = false;
      this.interacting = false;
      this.settling = false;
      this.settlementRequest = 0;
      this.manualRestart = false;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.dwell = Math.max(3, parseFloat(this.dataset.speed) || 6.5) * 1000;
      this.wantsAutoplay = this.dataset.autoplay === 'true';
      this.style.setProperty('--dxe-dwell', this.dwell + 'ms');

      // Hide every slide but the first from the tab order and from AT.
      this.slides.forEach((s, i) => {
        if (i !== 0) {
          s.inert = true;
          s.setAttribute('aria-hidden', 'true');
        }
      });

      const prev = this.querySelector('.dxe__arrow--prev');
      const next = this.querySelector('.dxe__arrow--next');
      if (prev) prev.addEventListener('click', () => this.step(-1, true));
      if (next) next.addEventListener('click', () => this.step(1, true));

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this.show(i, i > this.index ? 1 : -1, true));
      });

      this.pauseButton = this.querySelector('.dxe__pause');
      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          const strings = window.accessibilityStrings || {};
          this.pauseButton.setAttribute(
            'aria-label',
            this.userPaused
              ? strings.playSlideshow || 'Play slideshow'
              : strings.pauseSlideshow || 'Pause slideshow'
          );
          this.userPaused ? this.hold() : this.play();
        });
      }

      // Autoplay yields to the reader: keyboard focus, a hidden tab, or the
      // section being scrolled out of view all stop it. Mouse hover no longer
      // pauses (operator 2026-10-18, same rule as the homepage hero), and a
      // mouse click on an arrow or dot does not hold it either: only focus
      // that shows a focus ring (keyboard) holds, and only that resumes.
      this.addEventListener('focusin', (e) => {
        let keyboard = true;
        try {
          keyboard = e.target.matches(':focus-visible');
        } catch (err) {}
        if (!keyboard) return;
        this.keyboardHold = true;
        this.hold();
      });
      this.addEventListener('focusout', (e) => {
        if (!this.keyboardHold || this.contains(e.relatedTarget)) return;
        this.keyboardHold = false;
        this.play();
      });
      document.addEventListener('visibilitychange', () => (document.hidden ? this.hold() : this.play()));

      if ('IntersectionObserver' in window) {
        this.visible = false;
        // Fetch the first slide and the two it can move to while the section
        // is still PREFETCH_MARGIN below the fold, so on a slow connection the
        // photos are already arriving when the reader gets there.
        afterLoad(() =>
          new IntersectionObserver(
            (entries, obs) => {
              if (entries[0].isIntersecting && !this.seeded) {
                this.seeded = true;
                this.seed();
                obs.disconnect();
              }
            },
            { rootMargin: PREFETCH_MARGIN }
          ).observe(this)
        );
        // Autoplay only runs while the section is actually on screen. Being on
        // screen also seeds, in case the reader got here before window load.
        new IntersectionObserver(
          (entries) => {
            this.visible = entries[0].isIntersecting;
            if (this.visible && !this.seeded) {
              this.seeded = true;
              this.seed();
            }
            this.visible ? this.play() : this.hold();
          },
          { threshold: 0.2 }
        ).observe(this);
      } else {
        this.visible = true;
        this.seeded = true;
        this.seed();
      }

      /* Swipe. The stage CSS sets touch-action: pan-y, otherwise the browser
         claims the horizontal pan, fires pointercancel and the swipe is lost
         (touch never advanced before 2026-10-19). A mouse drag that ends on
         the photo link would also click through to the product, so the click
         after a swipe is swallowed. */
      const stage = this.querySelector('.dxe__stage');
      let startX = null;
      let startY = 0;
      let swiped = false;
      const pointers = new Set();
      let wheelActive = false;
      stage.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        pointers.add(e.pointerId);
        startX = pointers.size === 1 ? e.clientX : null;
        startY = e.clientY;
        swiped = false;
        this.interacting = true;
        this.beginInteraction();
      });
      this._onPointerUp = (e) => {
        if (!pointers.delete(e.pointerId)) return;
        const dx = startX === null ? 0 : e.clientX - startX;
        const dy = e.clientY - startY;
        startX = null;
        this.interacting = pointers.size > 0 || wheelActive;
        if (e.type !== 'pointercancel' && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          swiped = true;
          this.step(dx < 0 ? 1 : -1, true);
        } else this.settleTransition();
      };
      // Release outside the stage must also end the hold, without capturing
      // ordinary product-link taps or interfering with vertical panning.
      window.addEventListener('pointerup', this._onPointerUp);
      window.addEventListener('pointercancel', this._onPointerUp);
      stage.addEventListener(
        'click',
        (e) => {
          // detail 0 = a keyboard click: a touch swipe fires no click, so the
          // flag can still be set when Enter is pressed on the button later.
          if (!swiped || e.detail === 0) return;
          swiped = false;
          e.preventDefault();
          e.stopPropagation();
        },
        true
      );

      /* Trackpad: a two-finger sideways swipe arrives as wheel events with
         deltaX, never as pointer events. One gesture = one slide: momentum
         keeps sending events after the fingers lift, so the next step waits
         until the events stop. Vertical scrolling is left to the page, and
         preventDefault on the sideways ones stops the browser's back-swipe. */
      let wheelX = 0;
      let wheelLocked = false;
      let wheelDirection = 0;
      stage.addEventListener(
        'wheel',
        (e) => {
          if (e.ctrlKey || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
          e.preventDefault();
          wheelActive = true;
          this.interacting = true;
          this.beginInteraction();
          clearTimeout(this.wheelIdle);
          this.wheelIdle = setTimeout(() => {
            wheelActive = false;
            this.interacting = pointers.size > 0;
            wheelLocked = false;
            wheelX = 0;
            this.settleTransition();
          }, 200);
          const direction = Math.sign(e.deltaX);
          if (wheelLocked && direction !== wheelDirection) {
            wheelLocked = false;
            wheelX = 0;
          }
          wheelDirection = direction;
          if (wheelLocked) return;
          wheelX += e.deltaX;
          if (Math.abs(wheelX) > 40) {
            wheelLocked = true;
            this.step(wheelX > 0 ? 1 : -1, true);
          }
        },
        { passive: false }
      );

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.step(1, true);
        if (e.key === 'ArrowLeft') this.step(-1, true);
      });

      /* Theme editor: selecting a slide in the sidebar must show THAT slide.
         assets/theme-editor.js handles this only for `slideshow__slide` and
         returns early for anything else, so without this the operator edits
         slide 7 while the preview keeps autoplaying slide 1 past it. Handled
         here rather than in the shared file so no other section is touched. */
      if (window.Shopify && window.Shopify.designMode) {
        this._onBlockSelect = (e) => {
          const slide = e.target.closest ? e.target.closest('.dxe__slide') : null;
          if (!slide || !this.contains(slide)) return;
          const i = this.slides.indexOf(slide);
          if (i < 0) return;
          this.userPaused = true;
          this.hold();
          if (i !== this.index) this.show(i, i > this.index ? 1 : -1, false);
        };
        this._onBlockDeselect = (e) => {
          const slide = e.target.closest ? e.target.closest('.dxe__slide') : null;
          if (!slide || !this.contains(slide)) return;
          this.userPaused = false;
          this.play();
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);
        document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
      }

      this.play();
    }

    // The first slide, then the two it can move to once it has arrived, so
    // the photo the reader sees does not share bandwidth with two hidden ones.
    // The rest follow when the browser is idle: fetching only one slide ahead
    // meant a fast swipe or a phone on a slow line showed a photo still
    // arriving (operator 2026-10-19: "loading in from blank").
    seed() {
      this.hydrate(0);
      const first = this.slides[0].querySelector('img');
      const rest = () => {
        this.hydrate(1);
        this.hydrate(this.slides.length - 1);
        const all = () => this.slides.forEach((_, i) => this.hydrate(i));
        if ('requestIdleCallback' in window) requestIdleCallback(all, { timeout: 3000 });
        else setTimeout(all, 1500);
      };
      if (!first || first.complete) return rest();
      first.addEventListener('load', rest, { once: true });
      first.addEventListener('error', rest, { once: true });
    }

    // Swaps a parked data-src onto the element. Called for the slide being
    // shown and for its two neighbours, so the reader never waits on a fetch.
    hydrate(i) {
      const slide = this.slides[i];
      if (!slide) return;
      const img = slide.querySelector('img[data-src]');
      if (!img) return;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      delete img.dataset.src;
      delete img.dataset.srcset;
    }

    disconnectedCallback() {
      this.imageRequest = (this.imageRequest || 0) + 1;
      this.pending = null;
      ++this.settlementRequest;
      clearTimeout(this.wheelIdle);
      if (this._onPointerUp) {
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
      }
      if (this._onBlockSelect) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
      }
      this.stop();
    }

    show(next, direction, fromUser) {
      if (!this.slides[next]) return;
      if (fromUser) this.manualRestart = true;
      if (next === this.pending) {
        if (fromUser) this.pendingUser = true;
        return;
      }
      const request = this.imageRequest = (this.imageRequest || 0) + 1;
      this.pending = null;
      if (next === this.index) { this.settleTransition(); return; }

      const n = this.slides.length;
      this.hydrate(next);
      this.hydrate((next + 1) % n);
      this.hydrate((next - 1 + n) % n);

      // Retain the outgoing photo until decoding succeeds. A timeout or error
      // cancels this change; it must never reveal an unfinished photograph.
      this.pending = next;
      this.pendingUser = fromUser;
      this.hold();
      const img = this.slides[next].querySelector('img');
      this.readyImage(img).then((ready) => {
        if (!this.isConnected || request !== this.imageRequest) return;
        const user = this.pendingUser;
        this.pending = null;
        if (ready) this.swap(next, direction, user);
        this.settleTransition();
      });
    }

    readyImage(img) {
      if (!img) return Promise.resolve(true);
      img.loading = 'eager';
      return new Promise((resolve) => {
        let done = false;
        const finish = (ready) => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          img.removeEventListener('load', loaded);
          img.removeEventListener('error', failed);
          resolve(ready);
        };
        const failed = () => finish(false);
        const loaded = () => {
          if (!img.naturalWidth) return finish(false);
          const source = img.currentSrc || img.src;
          Promise.resolve().then(() => img.decode ? img.decode() : undefined).then(
            () => finish(img.complete && img.naturalWidth > 0 && source === (img.currentSrc || img.src)),
            failed
          );
        };
        const timer = setTimeout(failed, 15000);
        img.addEventListener('load', loaded);
        img.addEventListener('error', failed);
        if (img.complete) loaded();
      });
    }

    swap(next, direction, fromUser) {
      const out = this.slides[this.index];
      const into = this.slides[next];
      const returning = getComputedStyle(into).visibility === 'visible';

      out.classList.toggle('is-leaving', direction > 0);

      /* Park the incoming slide on the correct side BEFORE it animates in.
         Two things have to be true and the obvious one-liner only gets one of
         them. Flushing alone is not enough: a slide that has been shown before
         is still sitting at -2.2rem from its own exit, and merely dropping
         `is-leaving` starts a 760ms transform TRANSITION from -2.2rem back to
         +2.2rem. A bare `void offsetWidth` then commits whatever that
         in-flight transition is currently at, which is still close to
         -2.2rem, so the slide enters from the left anyway.
         Measured on the live theme before this line existed: advances 1-6 read
         +22px (those slides had never been shown, so nothing was animating),
         advances 7-10 read -22px once the carousel wrapped and started
         re-showing slides. Suppressing the transition for the reposition
         makes the jump instant, so the flush commits the real +2.2rem. */
      if (!returning) {
        into.style.transition = 'none';
        into.classList.toggle('is-leaving', direction < 0);
        void into.offsetWidth;
        into.style.transition = '';
        into.style.zIndex = String(this.layer = (this.layer || 1) + 1);
      } else {
        // Keep the composed frame: fade higher layers away instead of
        // raising an already opaque returning photo over the current fade.
        const layer = parseFloat(into.style.zIndex) || 0;
        this.slides.forEach(slide => {
          if ((parseFloat(slide.style.zIndex) || 0) > layer) {
            const image = slide.querySelector('img');
            if (image) image.style.transitionDelay = '0s';
          }
        });
      }
      const incomingImage = into.querySelector('img');
      if (incomingImage) incomingImage.style.transitionDelay = '';
      if (!returning) {
        const outgoingImage = out.querySelector('img');
        if (outgoingImage) outgoingImage.style.transitionDelay = '';
      }

      out.classList.remove('is-active');
      out.setAttribute('aria-hidden', 'true');
      out.inert = true;

      into.classList.add('is-active');
      into.setAttribute('aria-hidden', 'false');
      into.inert = false;

      this.dots[this.index].classList.remove('is-active');
      this.dots[this.index].removeAttribute('aria-current');
      this.dots[next].classList.add('is-active');
      this.dots[next].setAttribute('aria-current', 'true');

      this.index = next;

      // Restart the progress fill from zero on the newly active dot.
      const fill = this.dots[next].querySelector('.dxe__fill');
      if (fill) {
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      }

    }

    step(by, fromUser) {
      // From the slide already on its way in, so two quick presses move two.
      const from = this.pending != null ? this.pending : this.index;
      const next = (from + by + this.slides.length) % this.slides.length;
      this.show(next, by, fromUser);
    }

    beginInteraction() {
      this.manualRestart = true;
      ++this.settlementRequest;
      this.settling = false;
      // A decode started by autoplay must not commit under a new gesture.
      // Keep earlier manual intent so rapid swipes can build on its target.
      if (this.pending != null && !this.pendingUser) {
        ++this.imageRequest;
        this.pending = null;
      }
      this.hold();
    }

    settleTransition() {
      const request = ++this.settlementRequest;
      this.settling = true;
      this.hold();
      // Wait for actual visual transitions, not the slow decorative photo
      // zoom or the progress-fill animation. A newer swipe cancels this wait.
      const transitions = this.getAnimations ? this.getAnimations({ subtree: true }).filter(a =>
        a.transitionProperty === 'opacity' || a.transitionProperty === 'visibility' ||
        (a.transitionProperty === 'transform' && a.effect.target.classList.contains('dxe__slide'))
      ) : [];
      const done = this.getAnimations ? Promise.allSettled(transitions.map(a => a.finished)) :
        new Promise(resolve => setTimeout(resolve, 1500));
      done.then(() => {
        if (!this.isConnected || request !== this.settlementRequest) return;
        this.settling = false;
        this.play();
      });
    }

    play() {
      if (!this.isConnected) return;
      if (this.interacting || this.settling || this.pending != null || document.hidden ||
          this.userPaused || this.keyboardHold || this.reduce.matches || !this.wantsAutoplay || this.visible === false) return;
      this.stop();
      const delay = this.manualRestart ? 5000 : this.dwell;
      this.style.setProperty('--dxe-dwell', delay + 'ms');
      const fill = this.dots[this.index].querySelector('.dxe__fill');
      if (fill) {
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      }
      this.timer = setTimeout(() => {
        this.timer = null;
        this.manualRestart = false;
        this.step(1, false);
      }, delay);
      this.setAttribute('data-playing', 'true');
    }

    stop() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    hold() {
      this.stop();
      this.setAttribute('data-playing', 'false');
    }
  }

  customElements.define('dubai-exclusives-carousel', DubaiExclusivesCarousel);
})();
